import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useState } from 'react';
import { attachPresignedUrls } from 'src/services/api';
import { ProductFile, PRODUCT_FILES_QUERY_KEY } from '../pages/UploadPage/Uploader';

export type FileUploadStatus = {
  percentage: number;
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  /** Remaining time in seconds */
  remainingTime?: number;
};

/**
 * Tracks upload status of product files
 */
export type FileUploadStatusMap = Map<string, FileUploadStatus>;

export const useUploadFiles = (files: ProductFile[]) => {
  const [filesUploadStatus, setFilesUploadStatus] = useState<FileUploadStatusMap>(new Map());
  const [controller, setController] = useState(new AbortController());
  const queryClient = useQueryClient();

  const updateFilesProgress = useCallback((fileName: string, status: Partial<FileUploadStatus>) => {
    setFilesUploadStatus((prev) => {
      const newMap = new Map(prev);
      const newStatus = { ...newMap.get(fileName), ...status } as FileUploadStatus;
      newMap.set(fileName, newStatus);
      return newMap;
    });
  }, []);

  /* istanbul ignore next */
  const removeFileFromProgressMap = useCallback((fileName: string) => {
    setFilesUploadStatus((prev) => {
      const newMap = new Map(prev);
      newMap.delete(fileName);
      return newMap;
    });
  }, []);

  const mutation = useMutation(
    ['uploadFilesMutation'],
    async () => {
      // Create a new AbortController for each upload
      const newController = new AbortController();
      setController(newController);

      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        updateFilesProgress(file.name, { isLoading: true });
      }

      const filesWithUploadUrlsPromises = await attachPresignedUrls(files);
      const timeStarted = new Date();
      let resolvedFilesWithUploadUrls: ProductFile[] = [];
      try {
        resolvedFilesWithUploadUrls = await Promise.all(filesWithUploadUrlsPromises);
      } catch (error) /* istanbul ignore next */ {
        // eslint-disable-next-line no-restricted-syntax
        for (const file of files) {
          updateFilesProgress(file.name, { isError: true, isLoading: false });
        }
        throw error;
      }
      const uploadFilesPromises = resolvedFilesWithUploadUrls.map(async (productFile: ProductFile) => {
        if (productFile.presignedUrl) {
          try {
            return await axios
              .put(productFile.presignedUrl.url, productFile.file, {
                signal: newController.signal,
                onUploadProgress: /* istanbul ignore next */ (event) => {
                  // Calculate upload speed and time remaining
                  if (event?.loaded && event?.total) {
                    const timeElapsed = new Date().getTime() - timeStarted.getTime(); // Assuming that timeStarted is a Date Object
                    const uploadSpeed = event.loaded / (timeElapsed / 1000); // Upload speed in second
                    const secondsRemaining = (event.total - event.loaded) / uploadSpeed;
                    const percentage = ((event.loaded / event.total) * 100).toFixed(2);

                    updateFilesProgress(productFile.name, {
                      percentage: Number(percentage),
                      remainingTime: secondsRemaining,
                    });
                  }
                },
              })
              .then(
                /* istanbul ignore next */ () => {
                  updateFilesProgress(productFile.name, { isSuccess: true, isLoading: false });
                }
              );
          } catch (error) {
            /* istanbul ignore next */
            if (axios.isCancel(error)) {
              // Request was cancelled manually
              removeFileFromProgressMap(productFile.name);
              return;
            }
            /* istanbul ignore next */
            updateFilesProgress(productFile.name, { isError: true, isLoading: false });
            /* istanbul ignore next */
            throw error;
          }
        }
      });

      return Promise.all(uploadFilesPromises);
    },
    {
      onSuccess: /* istanbul ignore next */ () => {
        queryClient.invalidateQueries({ queryKey: [PRODUCT_FILES_QUERY_KEY] });
      },
    }
  );

  /* istanbul ignore next */
  const resetAndClear = () => {
    mutation.reset();
    setFilesUploadStatus(new Map());
  };

  return { ...mutation, filesUploadStatus, controller, reset: resetAndClear };
};
